import { useState, useEffect, useCallback } from 'react';
import { TrackedStore, StoreInfo } from '../types';

const KEY = 'tracked_stores_v1';
const EVENT = 'tracked-stores-changed';

function read(): TrackedStore[] {
  try { return JSON.parse(localStorage.getItem(KEY) ?? '[]'); }
  catch { return []; }
}

function write(stores: TrackedStore[]) {
  localStorage.setItem(KEY, JSON.stringify(stores));
  window.dispatchEvent(new Event(EVENT));
}

export function useTrackedStores() {
  const [stores, setStores] = useState<TrackedStore[]>(read);

  useEffect(() => {
    const sync = () => setStores(read());
    window.addEventListener(EVENT, sync);
    window.addEventListener('storage', sync);
    return () => {
      window.removeEventListener(EVENT, sync);
      window.removeEventListener('storage', sync);
    };
  }, []);

  const trackStore = useCallback((info: StoreInfo, url?: string) => {
    const current = read();
    if (current.some((s) => s.id === info.id)) return;
    write([...current, {
      id: info.id,
      name: info.name,
      region: info.region,
      country: info.country,
      rating: info.rating,
      url,
      savedAt: new Date().toISOString(),
    }]);
  }, []);

  const trackStoreByName = useCallback((name: string, region: string, url?: string) => {
    const id = name.toLowerCase().replace(/[^a-z0-9]+/g, '_');
    const current = read();
    if (current.some((s) => s.id === id)) return;
    write([...current, { id, name, region, country: region, rating: 0, url, savedAt: new Date().toISOString() }]);
  }, []);

  const untrackStore = useCallback((id: string) => {
    write(read().filter((s) => s.id !== id));
  }, []);

  const isTracked = useCallback((id: string) => stores.some((s) => s.id === id), [stores]);

  const isTrackedByName = useCallback(
    (name: string) => stores.some((s) => s.name.toLowerCase() === name.toLowerCase()),
    [stores],
  );

  return { stores, trackStore, trackStoreByName, untrackStore, isTracked, isTrackedByName };
}
